const PDP_SUBSCRIPTION_SELECTORS = {
  container: ".shopify_subscriptions_app_container",
  section: ".shopify_subscriptions_app_block",
  hiddenSection: "shopify_subscriptions_app_block--hidden",
  oneTimeInput: '[data-radio-type="one_time_purchase"]',
  sellingPlanInput: '[data-radio-type="selling_plan"]',
  subscriptionGroup: ".subscription_group",
  groupList: ".group_list",
  groupListItem: ".group_list li",
  titleWrapper: ".title_and_price_wrapper",
  atcPrice: ".atc-button .atc-price", 
};

const pdpSubscriptionSectionState = new WeakMap();
const PDP_SUBSCRIPTION_ICON = `
  <svg class="pdp-subscription-icon__svg" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <g clip-path="url(#pdp-subscription-icon-clip)">
      <path d="M18.7911 20.7159C19.6321 18.217 16.5946 14.4063 14.7038 12.9556C9.03249 8.60492 2.3572 18.5101 4.06486 20.8908C5.87749 23.4185 9.01976 21.3111 11.2427 21.4146C13.5493 21.5203 17.5283 24.4745 18.7911 20.7159Z" fill="currentColor"/>
      <path d="M14.7898 9.76939C17.977 11.4055 22.1898 3.97533 18.4817 1.58553C15.2408 -0.500206 10.698 7.66892 14.7898 9.76939Z" fill="currentColor"/>
      <path d="M7.74614 8.71134C11.5123 12.1398 14.4584 3.86687 10.3584 0.917418C6.66939 -1.73513 4.5561 5.80544 7.74614 8.71134Z" fill="currentColor"/>
      <path d="M3.32389 6.47839C-1.33922 5.98193 0.854808 15.4006 4.96658 14.4118C8.59998 13.5362 6.59855 6.82778 3.32389 6.47839Z" fill="currentColor"/>
      <path d="M17.8135 15.2432C20.5386 18.9628 26.3349 11.6816 23.1974 9.09136C20.4674 6.83882 15.8609 12.5802 17.8135 15.2432Z" fill="currentColor"/>
    </g>
    <defs>
      <clipPath id="pdp-subscription-icon-clip">
        <rect width="24" height="24" fill="white"/>
      </clipPath>
    </defs>
  </svg>
`; 
const PDP_SUBSCRIPTION_CHECK_ICON = `
  <svg class="pdp-subscription-check__svg" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true" focusable="false">
    <path d="M13.3 5L7.24998 11.5067L4.69998 8.78668L4 9.53333L7.25 13L14 5.74665L13.3 5Z" fill="black"/>
  </svg>
`;

function pdpSubscriptionCleanPrice(priceText) {
  if (!priceText) return "";
  return priceText.replace(/\s+[A-Z]{3}$/i, "").trim();
}

function pdpSubscriptionMoneyValue(priceText) {
  if (!priceText) return null;

  const value = Number.parseFloat(priceText.replace(/[^0-9.]/g, ""));
  return Number.isFinite(value) ? value : null;
}

function pdpSubscriptionFindVisibleSection(container) {
  return container.querySelector(
    `${PDP_SUBSCRIPTION_SELECTORS.section}:not(.${PDP_SUBSCRIPTION_SELECTORS.hiddenSection})`
  );
}

function pdpSubscriptionGetOneTimeInput(section) {
  return pdpSubscriptionSectionState.get(section)?.oneTimeInput
    || section?.querySelector(PDP_SUBSCRIPTION_SELECTORS.oneTimeInput)
    || null;
}

function pdpSubscriptionGetSellingInputs(section) {
  return pdpSubscriptionSectionState.get(section)?.sellingInputs
    || Array.from(section?.querySelectorAll(PDP_SUBSCRIPTION_SELECTORS.sellingPlanInput) || []);
}

function pdpSubscriptionGetCheckedSellingInput(section) {
  return pdpSubscriptionGetSellingInputs(section).find((input) => input.checked) || null;
}

function pdpSubscriptionGetOneTimeGroup(section) {
  return pdpSubscriptionSectionState.get(section)?.oneTimeGroup
    || Array.from(
      section?.querySelectorAll(PDP_SUBSCRIPTION_SELECTORS.subscriptionGroup) || []
    ).find((group) => group.querySelector(PDP_SUBSCRIPTION_SELECTORS.oneTimeInput));
}

function pdpSubscriptionGetSubscribeGroup(section) {
  return pdpSubscriptionSectionState.get(section)?.subscribeGroup
    || Array.from(
      section?.querySelectorAll(PDP_SUBSCRIPTION_SELECTORS.subscriptionGroup) || []
    ).find((group) => group.querySelector(PDP_SUBSCRIPTION_SELECTORS.sellingPlanInput));
}

function pdpSubscriptionGetPlanText(input) {
  const labelText = input
    ?.closest("label")
    ?.querySelector(".title_and_price_wrapper > span")
    ?.textContent?.trim();

  if (labelText) return labelText;
  return input?.getAttribute("aria-label") || "";
}

function pdpSubscriptionGetPlanFrequency(input) {
  const planText = pdpSubscriptionGetPlanText(input);
  const match = planText.match(/deliver every\s+(.+?)(?:,\s*\d+% off)?$/i);

  if (!match) return planText;

  const frequency = match[1].trim();
  return frequency.toLowerCase() === "month" ? "1 month" : frequency;
}

function pdpSubscriptionGetSavingsPercent(input) {
  if (!input) return "";

  const planText = `${pdpSubscriptionGetPlanText(input)} ${input.getAttribute("aria-label") || ""}`;
  const textMatch = planText.match(/(\d+)\s*%\s*off/i);

  if (textMatch) return textMatch[1];

  const currentPrice = pdpSubscriptionMoneyValue(input.dataset.variantPrice);
  const comparePrice = pdpSubscriptionMoneyValue(input.dataset.variantCompareAtPrice);

  if (!currentPrice || !comparePrice || comparePrice <= currentPrice) return "";

  return Math.round(((comparePrice - currentPrice) / comparePrice) * 100).toString();
}

function pdpSubscriptionEnsureSummary(subscribeGroup) {
  const header = subscribeGroup?.querySelector(PDP_SUBSCRIPTION_SELECTORS.titleWrapper);
  if (!header) return null;

  let summary = header.querySelector(".pdp-subscription-summary");

  if (!summary) {
    summary = document.createElement("div");
    summary.className = "pdp-subscription-summary";
    summary.innerHTML = `
      <span class="pdp-subscription-badge"></span>
      <div class="pdp-subscription-price-wrap">
        <span class="pdp-subscription-compare-price"></span>
        <span class="pdp-subscription-current-price"></span>
      </div>
    `;
    header.appendChild(summary);
  }

  return summary;
}

function pdpSubscriptionEnsureFrequencyLabel(subscribeGroup) {
  const list = subscribeGroup?.querySelector(PDP_SUBSCRIPTION_SELECTORS.groupList);
  if (!list) return;

  let label = subscribeGroup.querySelector(".pdp-subscription-frequency-label");

  if (!label) {
    label = document.createElement("p");
    label.className = "pdp-subscription-frequency-label";
    label.textContent = "Deliver every:";
    list.parentNode.insertBefore(label, list);
  }
}

function pdpSubscriptionEnsureBenefitsBlock(subscribeGroup) {
  const list = subscribeGroup?.querySelector(PDP_SUBSCRIPTION_SELECTORS.groupList);
  if (!list || subscribeGroup.querySelector(".pdp-subscription-benefits")) return;

  const benefits = document.createElement("div");
  benefits.className = "pdp-subscription-benefits";
  benefits.innerHTML = `
    <div class="pdp-subscription-benefit">
      <span class="pdp-subscription-check" aria-hidden="true">${PDP_SUBSCRIPTION_CHECK_ICON}</span>
      <span>Save 20%</span>
    </div>
    <div class="pdp-subscription-benefit">
      <span class="pdp-subscription-check" aria-hidden="true">${PDP_SUBSCRIPTION_CHECK_ICON}</span>
      <span>no commitments, cancel anytime</span>
    </div>
  `;

  list.insertAdjacentElement("afterend", benefits);
}

function pdpSubscriptionEnsureIcon(target, extraClassName) {
  if (!target || target.querySelector(".pdp-subscription-icon")) return;

  const icon = document.createElement("span");
  icon.className = `pdp-subscription-icon ${extraClassName}`.trim();
  icon.setAttribute("aria-hidden", "true");
  icon.innerHTML = PDP_SUBSCRIPTION_ICON;
  target.insertBefore(icon, target.firstChild);
}

function pdpSubscriptionEnsureLeadWrapper(wrapper, contentSelector, className) {
  if (!wrapper) return null;

  let lead = wrapper.querySelector(`.${className}`);
  if (lead) return lead;

  const content = wrapper.querySelector(contentSelector);
  if (!content) return wrapper;

  lead = document.createElement("div");
  lead.className = className;
  wrapper.insertBefore(lead, content);
  lead.appendChild(content);

  return lead;
}

function pdpSubscriptionEnsureSubscribeLeadWrapper(wrapper) {
  if (!wrapper) return null;

  let lead = wrapper.querySelector(".pdp-subscription-subscribe-lead");
  if (lead) return lead;

  const groupName = wrapper.querySelector(".group_name");
  const summary = wrapper.querySelector(".pdp-subscription-summary");
  const badge = summary?.querySelector(".pdp-subscription-badge");

  if (!groupName) return wrapper;

  lead = document.createElement("div");
  lead.className = "pdp-subscription-subscribe-lead";
  wrapper.insertBefore(lead, groupName);
  lead.appendChild(groupName);

  if (badge) {
    lead.appendChild(badge);
  }

  return lead;
}

function pdpSubscriptionUpdatePlanLabels(section) {
  pdpSubscriptionGetSellingInputs(section).forEach((input) => {
    const textElement = input
      .closest("label")
      ?.querySelector(".title_and_price_wrapper > span");

    if (textElement) {
      textElement.textContent = pdpSubscriptionGetPlanFrequency(input);
    }
  });
}

function pdpSubscriptionUpdateSelectedPlanState(section) {
  const state = pdpSubscriptionSectionState.get(section);
  const checkedInput = pdpSubscriptionGetCheckedSellingInput(section);

  (state?.planItems || section?.querySelectorAll(PDP_SUBSCRIPTION_SELECTORS.groupListItem) || []).forEach((item) => {
    const input = item.querySelector(PDP_SUBSCRIPTION_SELECTORS.sellingPlanInput);
    item.classList.toggle("pdp-subscription-plan--active", input === checkedInput);
  });
}

function pdpSubscriptionUpdateActiveState(section) {
  const oneTimeGroup = pdpSubscriptionGetOneTimeGroup(section);
  const subscribeGroup = pdpSubscriptionGetSubscribeGroup(section);
  const subscribeActive = Boolean(pdpSubscriptionGetCheckedSellingInput(section));

  if (oneTimeGroup) {
    oneTimeGroup.classList.toggle("pdp-subscription-option--active", !subscribeActive);
  }

  if (subscribeGroup) {
    subscribeGroup.classList.toggle("pdp-subscription-option--active", subscribeActive);
  }
}

function pdpSubscriptionUpdateSummary(section) {
  const state = pdpSubscriptionSectionState.get(section);
  const subscribeGroup = state?.subscribeGroup || pdpSubscriptionGetSubscribeGroup(section);
  const activePlan =
    pdpSubscriptionGetCheckedSellingInput(section) ||
    (state?.sellingInputs || pdpSubscriptionGetSellingInputs(section))[0];

  if (!subscribeGroup || !activePlan) return;

  const badge = state?.badge || subscribeGroup.querySelector(".pdp-subscription-badge");
  const comparePrice =
    state?.comparePrice || subscribeGroup.querySelector(".pdp-subscription-compare-price");
  const currentPrice =
    state?.currentPrice || subscribeGroup.querySelector(".pdp-subscription-current-price");
  const savingsPercent = pdpSubscriptionGetSavingsPercent(activePlan);
  const currentPriceText = pdpSubscriptionCleanPrice(activePlan.dataset.variantPrice);
  const comparePriceText = pdpSubscriptionCleanPrice(activePlan.dataset.variantCompareAtPrice);

  badge.textContent = savingsPercent ? `SAVE ${savingsPercent}%` : "Subscribe";
  currentPrice.textContent = currentPriceText;
  comparePrice.textContent = comparePriceText;
  comparePrice.style.display = comparePriceText ? "inline" : "none";
}

function pdpSubscriptionUpdateAtcPrice(section) {
  const atcPrice = document.querySelector(PDP_SUBSCRIPTION_SELECTORS.atcPrice);
  const activeInput =
    pdpSubscriptionGetCheckedSellingInput(section) ||
    pdpSubscriptionGetOneTimeInput(section);

  if (!atcPrice || !activeInput?.dataset?.variantPrice) return;
  atcPrice.textContent = pdpSubscriptionCleanPrice(activeInput.dataset.variantPrice);
}

function pdpSubscriptionUpdateOneTimePrice(section) {
  const state = pdpSubscriptionSectionState.get(section);
  const oneTimeInput = state?.oneTimeInput || pdpSubscriptionGetOneTimeInput(section);
  const oneTimePrice =
    state?.oneTimeGroup?.querySelector(".title_and_price_wrapper > .in_widget_price")
    || pdpSubscriptionGetOneTimeGroup(section)?.querySelector(".title_and_price_wrapper > .in_widget_price");

  if (!oneTimeInput?.dataset?.variantPrice || !oneTimePrice) return;
  oneTimePrice.textContent = pdpSubscriptionCleanPrice(oneTimeInput.dataset.variantPrice);
}

function pdpSubscriptionSyncSection(section) {
  if (!section) return;

  pdpSubscriptionUpdateActiveState(section);
  pdpSubscriptionUpdateSelectedPlanState(section);
  pdpSubscriptionUpdateSummary(section);
  pdpSubscriptionUpdateOneTimePrice(section);
  pdpSubscriptionUpdateAtcPrice(section);
}

function pdpSubscriptionSelectInput(input) {
  if (!input) return;

  if (input.checked) return;

  input.checked = true;
  input.dispatchEvent(new Event("change", { bubbles: true }));
}

function pdpSubscriptionActivateSubscribe(section, targetInput) {
  const input =
    targetInput ||
    pdpSubscriptionGetCheckedSellingInput(section) ||
    pdpSubscriptionGetSellingInputs(section)[0];

  if (!input) return;

  pdpSubscriptionSelectInput(input);
}

function pdpSubscriptionInitSection(section) {
  if (!section || section.dataset.pdpSubscriptionReady === "true") return;

  const oneTimeGroup = pdpSubscriptionGetOneTimeGroup(section);
  const subscribeGroup = pdpSubscriptionGetSubscribeGroup(section);
  const oneTimeInput = pdpSubscriptionGetOneTimeInput(section);
  const sellingInputs = pdpSubscriptionGetSellingInputs(section);
  const planItems = Array.from(
    section.querySelectorAll(PDP_SUBSCRIPTION_SELECTORS.groupListItem)
  );
  const groupName = subscribeGroup?.querySelector(".group_name");
  const oneTimeText = oneTimeGroup?.querySelector(".one_time_purchase_option_app_block span");

  if (!oneTimeGroup || !subscribeGroup || !oneTimeInput) return;

  section.dataset.pdpSubscriptionReady = "true";

  oneTimeGroup.classList.add("pdp-subscription-option", "pdp-subscription-option--one-time");
  subscribeGroup.classList.add("pdp-subscription-option", "pdp-subscription-option--subscribe");

  if (oneTimeText) oneTimeText.textContent = "One-time";
  if (groupName) groupName.textContent = "Subscribe and";

  pdpSubscriptionEnsureSummary(subscribeGroup);
  pdpSubscriptionEnsureIcon(
    pdpSubscriptionEnsureLeadWrapper(
      oneTimeGroup.querySelector(".title_and_price_wrapper"),
      ".one_time_purchase_option_app_block",
      "pdp-subscription-option__lead"
    ),
    "pdp-subscription-icon--one-time"
  );
  pdpSubscriptionEnsureIcon(
    pdpSubscriptionEnsureSubscribeLeadWrapper(
      subscribeGroup.querySelector(".title_and_price_wrapper")
    ),
    "pdp-subscription-icon--subscribe"
  );

  pdpSubscriptionUpdatePlanLabels(section);
  pdpSubscriptionEnsureFrequencyLabel(subscribeGroup);
  pdpSubscriptionEnsureBenefitsBlock(subscribeGroup);

  pdpSubscriptionSectionState.set(section, {
    oneTimeGroup,
    subscribeGroup,
    oneTimeInput,
    sellingInputs,
    planItems,
    badge: subscribeGroup.querySelector(".pdp-subscription-badge"),
    comparePrice: subscribeGroup.querySelector(".pdp-subscription-compare-price"),
    currentPrice: subscribeGroup.querySelector(".pdp-subscription-current-price"),
  });

  subscribeGroup.addEventListener("click", (event) => {
    const planItem = event.target.closest("li");

    if (planItem) {
      pdpSubscriptionActivateSubscribe(
        section,
        planItem.querySelector(PDP_SUBSCRIPTION_SELECTORS.sellingPlanInput)
      );
      return;
    }

    pdpSubscriptionActivateSubscribe(section);
  });

  oneTimeGroup.addEventListener("click", () => {
    pdpSubscriptionSelectInput(oneTimeInput);
  });

  [...sellingInputs, oneTimeInput].forEach((input) => {
    input.addEventListener("change", () => {
      pdpSubscriptionSyncSection(section);
    });
  });

  pdpSubscriptionSyncSection(section);
}

function pdpSubscriptionInitAll() {
  document.querySelectorAll(PDP_SUBSCRIPTION_SELECTORS.container).forEach((container) => {
    const sections = container.querySelectorAll(PDP_SUBSCRIPTION_SELECTORS.section);
    const visibleSection = pdpSubscriptionFindVisibleSection(container);

    sections.forEach((section) => pdpSubscriptionInitSection(section));
    pdpSubscriptionSyncSection(visibleSection);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", pdpSubscriptionInitAll, { once: true });
} else {
  pdpSubscriptionInitAll();
} 